import React, { useState, useEffect } from 'react';

import { usePaymentApiV1 } from '../../../../clients/quipli-service/payment/payment-api-v1';
import { Typography } from '../../../../components/typography/typography';
import { Modal } from '../../../../components/modal/modal';
import { ecommerceToast } from '../../../../util/toasts';
import { useIntervalWithTimeout } from '../../../../hooks/use-interval-with-timeout';

export const StripeTerminalComponent = ({ locationId, close }: { locationId: number; close?: () => void }) => {
  const { addTerminalCardUser, completeCardSetupUser, getSetupAttemptStatusUser } = usePaymentApiV1();

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [setupIntent, setSetupIntent] = useState<string>();
  const [setupIntentStatus, setSetupIntentStatus] = useState<string>('pending');
  const [timerRunning, setTimerRunning] = useState<boolean>(true);

  useEffect(() => {
    addTerminalCardUser(locationId)
      .then((intent) => {
        setSetupIntent(intent.setupIntentId);
      })
      .catch((err) => {
        console.log(err);
        ecommerceToast('Could not connect to reader', 'error');
      });
  }, []);

  useEffect(() => {
    if (setupIntentStatus === 'succeeded') {
      completeSetup();
    } else if (['pending', 'processing'].includes(setupIntentStatus)) {
      return;
    } else if (['failed', 'abandoned'].includes(setupIntentStatus)) {
      ecommerceToast('Payment failed, please try again', 'error');
      closeModal();
      return;
    }
    return;
  }, [setupIntentStatus]);

  useIntervalWithTimeout(
    async () => {
      if (setupIntent && ['pending', 'processing'].includes(setupIntentStatus)) {
        const setupIntentStatus = await getSetupAttemptStatusUser(setupIntent);
        setSetupIntentStatus(setupIntentStatus.status);
        return;
      }
    },
    3000,
    timerRunning,
    180000,
    () => {
      if (setupIntentStatus !== 'succeeded') {
        ecommerceToast('Reader session has timed out, please try again!', 'error');
      }
      closeModal();
    },
  );

  const closeModal = () => {
    document.body.dispatchEvent(new CustomEvent('customer-terminal-complete'));
    setTimerRunning(false);
    setIsOpen(false);
    close && close();
  };

  const completeSetup = () => {
    if (setupIntent) {
      completeCardSetupUser(setupIntent)
        .then(() => ecommerceToast('Card successfully added!'))
        .catch((err) => ecommerceToast(err, 'error'));
    }
    closeModal();
  };

  return (
    <div id="payTerminal">
      <Typography type={'text-md'}>Please swipe, tap, or insert your card to complete the transaction.</Typography>

      {isOpen && (
        <Modal title={'Swipe or insert card now'} isOpen={isOpen} close={closeModal}>
          <div
            className="dialog_content"
            style={{ textAlign: 'left', maxWidth: '500px', maxHeight: '600px', overflow: 'auto' }}
            data-animation="pop"
          >
            <div className="mb-0">
              <div className="modal-body">
                <p className="text-center">
                  Please <b>swipe, insert,</b> or <b>tap</b> your card now to add a payment method.
                </p>
                <img
                  height="350px"
                  width="450px"
                  src="https://cdn.dribbble.com/users/2874108/screenshots/14050299/media/97c711d0ab40503970efb670a0fb778f.gif"
                />
              </div>
            </div>
          </div>
          <div className="dialog-actions flex justify-start gap-2">
            <button className="button button-small button-block button-secondary" onClick={() => closeModal()}>
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};
